import { useCallback } from "react";

import { useCurrentUser } from "@/contexts";
import { isPlatformManager } from "@/utils";
import { hasCapability } from "@/utils/capability";
import { isFederationOwner } from "@/utils/federation";

import { useOrganizationCapabilities } from "../capability/useOrganizationCapabilities";
import { usePlatformCapabilities } from "../capability/usePlatformCapabilities";

/**
 * Structural minimum required by the kept per-platform predicates: identity + the
 * slim owner mainManager ref. Both legacy `BasePlatform & PlatformWithOwner` and
 * the REST-derived `AccessiblePlatform` satisfy this shape.
 */
type PlatformPermissionInput = {
  id: string;
  owner: { mainManager?: { id: string } | null } | null;
};

/**
 * Org create / view / update / detach are strict RBAC (`ORG:CREATE` on the platform
 * set, `ORG:READ` / `ORG:WRITE` on the pair-scoped org set, `ORG:DETACH` on the
 * platform set) — the S0 grants give every legacy member the matching keys. See
 * docs/capabilities.md.
 *
 * The pair-scoped org set also drives the update-page section gates minted for
 * the manager RBAC feature:
 *  - `canViewOrganizationManagers` / `canEditOrganizationManagers` —
 *    `ORG_MANAGER:READ` / `ORG_MANAGER:WRITE` (managers list).
 *  - `canEditOrganizationOwner` — `ORG_OWNER:WRITE` (main-manager / ownership
 *    change).
 *
 * Fees are the exception: an organization's (federation's) fees **are** the
 * platform's fees — the org Fees tab only ever edits the current platform's fee
 * bucket — so `canViewPlatformFees` / `canEditPlatformFees` read the *same* keys
 * the platform Fees tab reads, `PLATFORM_FEE:READ` / `PLATFORM_FEE:WRITE`. There
 * is no `ORG_FEE:*` key any more: a platform manager (whose bundle holds
 * `PLATFORM_FEE:READ` and **no** fee-write key anywhere) can view fees on both
 * editors and edit them on neither. Tournated's own fee columns stay the
 * super-admin `canEditTournatedFees` affordance.
 *
 * The org feature flags (enableLicenses / enableCertificates /
 * enableApplicationForms / enableRankings — the Capabilities tab and the create
 * wizard's permissions step) are a **hosting-platform** decision: the backend
 * enforces `ORG_FEATURE_FLAG:WRITE` at the PLATFORM node of the platform hosting
 * the federation, so `canEditOrganizationFeatureFlags` reads the *platform* set
 * (like `canCreateOrganization` / `canManagePlatformOrganizations`), not the
 * pair-scoped org set — the key never appears there.
 *
 * The remaining members have **no capability key that models them** and stay
 * legacy predicates (never OR-bridged — they were never cut over). They are only
 * consumed by the **create** wizard, which gates over several platforms at once
 * and has no per-platform capability set to check:
 *  - `canEditTournatedFees` / `canChangeLegalType` — super-admin-only affordances
 *    (Tournated's own fee columns / legal-entity switch); no vocabulary key.
 *  - `canEditPartnerFees` — a **per-platform** predicate used as an array filter
 *    over several platforms at once (a wizard picks which platforms the caller
 *    may configure); the FE only holds the active scope's capability set, so an
 *    arbitrary platform cannot be capability-checked.
 *  - `canUpdateMainManager` — org-ownership change; no FE vocabulary key.
 */
export const useOrganizationPermissions = () => {
  const currentUser = useCurrentUser();
  const platformCapabilities = usePlatformCapabilities();
  const organizationCapabilities = useOrganizationCapabilities();

  const canCreateOrganization = hasCapability(platformCapabilities, "ORG:CREATE");

  // The capability arms are pair-correct: `useOrganizationCapabilities` is fetched
  // with the current platform's id, so `ORG:READ` / `ORG:WRITE` granted at another
  // attached platform does not open these gates. `canViewOrganization` makes the
  // update page reachable read-only; `canUpdateOrganization` unlocks editing.
  const canViewOrganization = hasCapability(organizationCapabilities, "ORG:READ");
  const canUpdateOrganization = hasCapability(organizationCapabilities, "ORG:WRITE");

  // Attaching / detaching an organization is a platform-level operation.
  const canManagePlatformOrganizations = hasCapability(platformCapabilities, "ORG:DETACH");

  // The org feature-flag slice (enableLicenses / enableCertificates /
  // enableApplicationForms / enableRankings) is a hosting-platform decision: the
  // backend gates it on ORG_FEATURE_FLAG:WRITE at the PLATFORM node, so it is
  // read off the platform set — it never appears in the pair-scoped org set.
  const canEditOrganizationFeatureFlags = hasCapability(
    platformCapabilities,
    "ORG_FEATURE_FLAG:WRITE"
  );

  // Fees are platform fees (no ORG_FEE:* key exists any more): the org Fees tab
  // edits the *current platform's* fee bucket for this federation, so it shares
  // PLATFORM_FEE:READ / :WRITE with the platform Fees tab.
  //
  // Accepted from EITHER set, matching where the backend evaluates each surface:
  // the federation fee read/write is enforced at the federation's ORG node, and
  // that node's set is the superset — it folds in the current platform's
  // ancestor keys (so a platform owner's PLATFORM_FEE:WRITE reaches it) *and*
  // carries keys granted directly at the org node (the organization_manager
  // bundle holds PLATFORM_FEE:READ there, its read-only fee visibility). The
  // platform-set arm keeps the platform-node holder covered even if a set stops
  // folding ancestors; it never widens anyone, because a platform-node
  // PLATFORM_FEE holder is by definition allowed on this same fee surface.
  const canViewPlatformFees =
    hasCapability(organizationCapabilities, "PLATFORM_FEE:READ") ||
    hasCapability(platformCapabilities, "PLATFORM_FEE:READ");
  const canEditPlatformFees =
    hasCapability(organizationCapabilities, "PLATFORM_FEE:WRITE") ||
    hasCapability(platformCapabilities, "PLATFORM_FEE:WRITE");

  // Update-page section gates (pair-scoped org set). Managers and ownership are
  // each their own capability now — the update page reads these directly.
  const canViewOrganizationManagers = hasCapability(organizationCapabilities, "ORG_MANAGER:READ");
  const canEditOrganizationManagers = hasCapability(organizationCapabilities, "ORG_MANAGER:WRITE");
  const canEditOrganizationOwner = hasCapability(organizationCapabilities, "ORG_OWNER:WRITE");

  // Super-admin-only affordance — no capability key models editing Tournated's
  // own fee columns.
  const canEditTournatedFees = currentUser.role === "superAdmin";

  // Per-platform predicate (array filter over several platforms) — no capability
  // key, and the FE cannot capability-check an arbitrary platform. Stays legacy.
  const canEditPartnerFees = useCallback(
    (platform: PlatformPermissionInput) => {
      return (
        isFederationOwner(currentUser, platform.owner) ||
        isPlatformManager(platform, currentUser) ||
        currentUser.role === "superAdmin"
      );
    },
    [currentUser]
  );

  // Org-ownership change — no FE vocabulary key models it. Stays legacy.
  const canUpdateMainManager = useCallback(
    (
      platform: { id: string; owner: { id: string; mainManager?: { id: string } | null } | null },
      organization?: { id: string }
    ) => {
      if (currentUser.role === "superAdmin") {
        return true;
      }

      const organizationOwnsPlatform = !!organization && platform.owner?.id === organization.id;

      // When the organization being edited is the platform owner, only the owner
      // of that organization may change the main manager. Otherwise any platform
      // manager can.
      if (organizationOwnsPlatform) {
        return isFederationOwner(currentUser, platform.owner);
      }

      return !!isPlatformManager(platform, currentUser);
    },
    [currentUser]
  );

  // Super-admin-only affordance — no capability key models the legal-type switch.
  const canChangeLegalType = currentUser.role === "superAdmin";

  return {
    canCreateOrganization,
    canViewOrganization,
    canUpdateOrganization,
    canEditTournatedFees,
    canEditPartnerFees,
    canUpdateMainManager,
    canChangeLegalType,
    canManagePlatformOrganizations,
    canEditOrganizationFeatureFlags,
    canViewPlatformFees,
    canEditPlatformFees,
    canViewOrganizationManagers,
    canEditOrganizationManagers,
    canEditOrganizationOwner,
  };
};
